import { useCallback, useEffect, useRef, useState } from "react";
import { getBulkDrift } from "../../utils/requests/subitemDiscrepancyRequests";
import type { BulkDriftRow } from "./subitemBulk.types";

/**
 * The list behind the bulk scan's counts — what a confirmed resync will actually change.
 *
 * NOT SWR, unlike every other read in this feature, and the difference is deliberate. SWR's model
 * is "one key, one answer, revalidated"; this is an append-only walk through a paged snapshot the
 * user pulls in a page at a time. Expressing that in SWR means either a key per cursor (so the
 * accumulated list lives outside the cache anyway) or a mutating key (so every page invalidates
 * the last). Neither buys anything here: the snapshot is immutable for the life of the job, so
 * there is nothing to revalidate.
 *
 * FETCHES NOTHING UNTIL ASKED. The panel renders this collapsed, and a scan whose summary the
 * user simply accepts should cost no extra request at all. `loadMore` is the only thing that
 * issues one.
 */
export const useBulkDrift = (token: string | null, jobId: string | null) => {
  const [rows, setRows] = useState<BulkDriftRow[]>([]);
  const [boardNames, setBoardNames] = useState<Record<string, string>>({});
  const [total, setTotal] = useState<number | null>(null);
  const [nextCursor, setNextCursor] = useState<number | null>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * Guards a second concurrent fetch for the same cursor — a double click on "Show details", or
   * a scroll handler firing twice before the first response lands. Without it both requests
   * append the same page and the list shows every row twice.
   *
   * A ref, not state: it must be readable and writable synchronously inside `loadMore`, and a
   * state update would not be visible to the second call in the same tick.
   */
  const inFlight = useRef(false);

  /*
   * Resets when the JOB changes, so a second scan never renders the first one's plan.
   *
   * Keyed on `jobId` rather than on the panel unmounting, because the panel does not unmount: the
   * bulk controller is hoisted to the page and survives tab switches, so a new scan replaces the
   * job id underneath a mounted list.
   */
  useEffect(() => {
    setRows([]);
    setBoardNames({});
    setTotal(null);
    setNextCursor(0);
    setError(null);
    inFlight.current = false;
  }, [jobId]);

  const loadMore = useCallback(async () => {
    if (!token || !jobId || nextCursor === null || inFlight.current) return;

    inFlight.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const page = await getBulkDrift(token, jobId, nextCursor);

      setRows((current) => [...current, ...page.rows]);
      // MERGED, not replaced. Each page resolves only the boards ITS rows mention, so replacing
      // would drop the names for every row already on screen and silently regress them to ids.
      setBoardNames((current) => ({ ...current, ...page.boardNames }));
      setTotal(page.total);
      setNextCursor(page.nextCursor);
    } catch (requestError: any) {
      // The list is an aid to a decision, never the decision itself — the summary above it still
      // states what the user is consenting to. So a failure here reports itself and leaves the
      // rows already loaded on screen, rather than emptying the list or failing the panel.
      setError(requestError?.message ?? "Could not load the details.");
    } finally {
      inFlight.current = false;
      setIsLoading(false);
    }
  }, [token, jobId, nextCursor]);

  return {
    rows,
    boardNames,
    total,
    isLoading,
    error,
    hasMore: nextCursor !== null,
    /** True before the first page has been asked for — distinct from "loaded and empty". */
    isUnloaded: total === null,
    loadMore,
  };
};

export default useBulkDrift;
